// src/components/VaccineSaleList.js
import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch } from "@fortawesome/free-solid-svg-icons";
import DataTable from "./AppDataTable";
import { tableCustomStyles } from "../themes/TableStyles";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";
import { StandardHeader } from "../themes/StandardHeader";

const VaccineSaleList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "เลขที่เอกสาร",
      selector: (row) => row.TransactionID,
      sortable: true,
      width: "250px",
    },
    {
      name: "วันที่จำหน่าย",
      selector: (row) => new Date(row.RequestDate).toLocaleString(),
      sortable: true,
      width: "250px",
    },
    { name: "ชื่อเกษตรกร", selector: (row) => row.FarmerName, sortable: true },
    {
      name: "จุดจำหน่าย",
      selector: (row) => {
        const warehouse = warehouses.find(
          (w) => w.WarehouseID === row.PickupPoint
        );
        return warehouse ? warehouse.WarehouseName : row.PickupPoint || "-";
      },
      sortable: true,
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.View
          icon={faFileLines}
          onClick={() => navigate(`/vaccinesale/${row.TransactionID}`)}
          label="ดูรายละเอียด"
          width="180px"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchWarehouses = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchSales = async () => {
    setIsLoading(true);
    try {
      await fetchWarehouses();
      const response = await apiService({
        method: "POST",
        url: "/getVaccineSaleOrdersList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.VaccineSaleOrdersList || [];
        setSales(data);
        setFilteredSales(data);
      }
    } catch (error) {
      console.error("Error fetching sales:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchSales();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    if (!value) {
      setFilteredSales(sales);
      return;
    }

    const searchTerms = value.split(" ").filter((term) => term.length > 0);
    const filtered = sales.filter((row) => {
      const warehouseName =
        warehouses.find((w) => w.WarehouseID === row.PickupPoint)
          ?.WarehouseName ||
        row.PickupPoint ||
        "";
      return searchTerms.every((term) =>
        [
          row.TransactionID.toLowerCase(),
          row.FarmerName.toLowerCase(),
          warehouseName.toLowerCase(),
        ].some((field) => field.includes(term))
      );
    });
    setFilteredSales(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilteredSales(sales);
  };

  const handleCreateNew = () => {
    navigate("/vaccinesale/new");
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <StandardHeader
          title="รายการจำหน่ายวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />
        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
        >
          {sales.length > 0 && (
            <InputGroup
              w={{ base: "100%", md: "50%", lg: "30%" }}
              mb={{ base: 4, md: 0 }}
            >
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาใบจำหน่าย..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
                mt={1}
              />
              <IconButton.Clear onClick={handleClearSearch} ml={5} />
            </InputGroup>
          )}
          <IconButton.Add onClick={handleCreateNew} label="เพิ่มรายการใหม่" />
        </HStack>
        {isLoading ? (
          <Spinner size="md" />
        ) : sales.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลใบจำหน่ายวัคซีน
          </Text>
        ) : filteredSales.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredSales}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default VaccineSaleList;
